import { ListBaseModel } from '@/api/models';
import { t } from '@/i18n';
import { DateRangeSchema, jsonType } from '@/utils';
import { z } from 'zod';

/// //////////////////////////////////////////////////
/// Enum
/// /////////////////////////////////////////////////

export const batchResultStatusType = z.enum([
  'success',
  'processing',
  'failure',
]);

const rsBatchCsvBulkUpdateTargetType = z.enum([
  'payment-group-upload', // 支出先グループアップロード
  'contractor-payment-upload', // 国庫債務負担行為等による契約先アップロード
]);

export const rsBatchCsvDownloadTargetType = z.enum([
  'rs-project-list', // RS事業一覧
  'ss-project-list', // SS事業一覧
  'ks-project-list', // KS事業一覧
  'rs-report1', // 様式1
  'rs-report2', // 様式2
  'rs-report3', // 様式3
  'rs-report4', // 様式4
  'rs-report5', // 様式5
  'ks-standard-list', // 基金一覧
  'ks-local-government-list-A', // 地方公共団体等基金一覧A表
  'ks-local-government-list-B', // 地方公共団体等基金一覧B表
  'ks-investment-list', // 出資状況表一覧
  'payment-group-upload', // 支出先グループアップロード
  'contractor-payment-upload', // 国庫債務負担行為等による契約先アップロード
  'organizations-list', // 組織一覧
  'users_upload_list', // ユーザーアップロード結果
  'users_download_list', // ユーザー一覧
]);
export const rsBatchPdfDownloadTargetType = z.enum([
  'rs-report1', // 様式1
  'rs-report2', // 様式2
  'rs-report3', // 様式3
  'rs-report4', // 様式4
  'rs-report5', // 様式5
  'ks-standard-list', // 基金一覧
  'ks-local-government-list-A', // 地方公共団体等基金一覧A表
  'ks-local-government-list-B', // 地方公共団体等基金一覧B表
  'ks-investment-list', // 出資状況表一覧
]);

export const batchTargetLabel = z.enum([
  ...rsBatchCsvBulkUpdateTargetType.options,
  ...rsBatchCsvDownloadTargetType.options,
  ...rsBatchPdfDownloadTargetType.options,
]);

export const batchType = z.enum([
  'bulk-copy-project',
  'bulk-lock',
  'bulk-unlock',
  'bulk-publish',
  'bulk-unpublish',
  'bulk-update-organization-excluded-flags',
  'json-output-for-csv-download',
  'json-output-for-pdf-download',
  'csv-bulk-upload',
  'bulk-print',
]);
export const BatchTypeLabelModifier = {
  'bulk-copy-project': t('bulk-copy-project'),
  'bulk-lock': t('bulk-lock'),
  'bulk-unlock': t('bulk-unlock'),
  'bulk-publish': t('bulk-publish'),
  'bulk-unpublish': t('bulk-unpublish'),
  'bulk-update-organization-excluded-flags': t(
    'title-bulk-update-excluded-flags',
  ),
  'json-output-for-csv-download': t('export-csv'),
  'json-output-for-pdf-download': t('export-pdf'),
  'csv-bulk-upload': t('bulk-create'),
  'bulk-print': t('bulk-print'),
} as const satisfies Record<z.infer<typeof batchType>, string>;

const batchStatusType = z.enum(['success', 'processing', 'failure']);
export const BatchStatusLabelModifier = {
  success: t('batch.status-success'),
  processing: t('batch.status-processing'),
  failure: t('batch.status-failure'),
} as const satisfies Record<z.infer<typeof batchStatusType>, string>;

export const batchResultType = z.enum([
  'success',
  'error',
  'error_bad_request',
]);

/// //////////////////////////////////////////////////
/// Model
/// /////////////////////////////////////////////////

const BatchBaseModel = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  type: batchType,
  target: z.union([
    z
      .string()
      .nullable()
      .transform((value) => (value === null ? '' : value)),
    batchTargetLabel,
  ]),
  params: jsonType,
  taskResultId: z.number(),
  processedCount: z.number().nullish(),
  targetCount: z.number().nullish(),
  taskResult: z.object({
    id: z.number(),
    taskId: z.string().uuid(),
    result: z
      .object({
        batchResult: batchResultType.nullish(),
        targetCount: z.number().nullish(),
        bulkProcessedCount: z.number().nullish(),
        bulkSkippedCount: z.number().nullish(),
        errorDetails: z
          .object({
            id: z.string().uuid().nullish(),
            name: z.string().nullish(),
            errorCode: z.string().nullish(),
          })
          .array()
          .nullish(),
      })
      .nullable(),
    dateCreated: z.string(),
    dateDone: z.string(),
  }),
  isDownloadFileOutdated: z.boolean(),
  hasDownloadFile: z.boolean(),
  /**
   * @description
   * **Conditions that make it impossible to download (OR)**
   *  - Download file does not exist
   *  - Download file is outdated
   *  - The version of batch result is not the latest
   */
  canDownloadFile: z.boolean(),
  downloadExpiresAt: z.string().datetime().nullable(),
  status: batchResultStatusType,
});

export const BatchModel = BatchBaseModel;

export const BatchesListModel = ListBaseModel.extend({
  results: BatchBaseModel.array(),
});

export const BulkResponseModel = z.object({
  status: z.enum(['success']),
  requestedCount: z.number().int(),
});

export const PaymentGroupUploadErrorModel = z.object({
  csv: z.string().array().array(),
});

/// //////////////////////////////////////////////////
/// Schema
/// /////////////////////////////////////////////////

export const BatchSearchFormSchema = z
  .object({
    batchType: z.string().nullish(),
    createdDateRange: DateRangeSchema.nullish(),
  })
  .partial();

export const PaymentGroupUploadParamsSchema = z.object({
  // FIXME: change field name `projectPk` to `projectId`
  projectPk: z.string().uuid(),
  taskId: z.string().uuid(),
});
